import { useEffect, useMemo, useRef } from "react";

import { useQuery } from "@tanstack/react-query";

import { TagInfo } from "@/apis/table/types";
import queries from "@/queries";
import {
  AxisWithChildrenBaseType,
  AxisWithChildrenType,
  Dimension,
  MemberType,
  Role,
  TaxonomyTagType,
} from "@/types/mapping";

import { useSelectedTable, useSelectedTags } from "@/components/mapping/hooks";
import { tagKeyWithRole } from "@/components/mapping/utils";

import { useWorkbookId } from "@/hooks/use-workbook";
import { factAddress } from "@/jspreadsheet/utils";
import { useMappingStore } from "@/stores/mapping";
import { AccountingPeriod, Accumulated, TargetYear } from "@/types/enums";
import { MemberTagsViewType, MemberViewType } from "./types";

const EMPTY_TAGS: TagInfo[] = [];

export class TagsSingleSelectedManager {
  private selectedTags: TagInfo[];

  constructor(selectedTags: TagInfo[]) {
    this.selectedTags = selectedTags;
  }

  public getAxisTagsMap = (
    axesArray: MemberTagsViewType[],
    dimension: Dimension,
    tagsToAdd?: TagInfo[],
  ) => {
    const tagsByRoleAxis = new Map<string, TagInfo[]>();

    axesArray.forEach((axis) => {
      if (!axis.multiSelect) {
        const memberNames = this._collectMemberNames(axis);
        const tagsInAxis = this._getTagsInAxis(
          this.selectedTags,
          dimension,
          memberNames,
        );

        tagsByRoleAxis.set(
          `${axis.id}-${axis.role?.key ?? "NONE"}`,
          tagsInAxis,
        );
      }
    });

    if (tagsToAdd) {
      tagsToAdd.forEach((tag) => {
        if (
          tagsByRoleAxis.has(`${tag.parent?.id}-${tag.roleKey ?? "NONE"}`) &&
          !tagsByRoleAxis
            .get(`${tag.id}-${tag.roleKey ?? "NONE"}`)
            ?.some((t) => t.id === tag.id)
        ) {
          tagsByRoleAxis
            .get(`${tag.parent?.id}-${tag.roleKey ?? "NONE"}`)
            ?.push(tag);
        } else if (
          !tagsByRoleAxis.has(`${tag.parent?.id}-${tag.roleKey ?? "NONE"}`)
        ) {
          tagsByRoleAxis.set(`${tag.parent?.id}-${tag.roleKey ?? "NONE"}`, [
            tag,
          ]);
        }
      });
    }

    if (dimension === "LINE_ITEM") {
      const lineItemTags = new Map<string, TagInfo[]>();
      const flattenedTags = Array.from(tagsByRoleAxis.values()).flat();
      if (lineItemTags) {
        lineItemTags.set("LINE_ITEM", flattenedTags);
      }

      if (tagsToAdd) {
        tagsToAdd.forEach((tag) => {
          if (!lineItemTags.get("LINE_ITEM")?.some((t) => t.id === tag.id)) {
            lineItemTags.get("LINE_ITEM")?.push(tag);
          }
        });
      }

      return lineItemTags;
    }

    return tagsByRoleAxis;
  };

  private _collectMemberNames = (axis: MemberTagsViewType) => {
    const memberNames = new Set<string>();

    this._traverse(axis.children, memberNames, axis.role);

    return memberNames;
  };

  private _traverse = (
    members: MemberViewType[],
    memberNames: Set<string>,
    role: Role | null,
  ): void => {
    members.forEach((member) => {
      memberNames.add(
        tagKeyWithRole(member.prefix, member.name, role?.key ?? "NONE"),
      );
      this._traverse(member.children, memberNames, role);
    });
  };

  private _getTagsInAxis = (
    selectedTags: TagInfo[],
    dimension: Dimension,
    memberNames: Set<string>,
  ) => {
    const memberType = dimension === "LINE_ITEM" ? "LINE_ITEM" : "MEMBER";
    return selectedTags.filter(
      (tag) =>
        tag.type === memberType &&
        memberNames.has(
          tagKeyWithRole(tag.prefix, tag.name, tag.roleKey ?? "NONE"),
        ),
    );
  };

  public findTagsToRemove = (tagsByAxis: Map<string, TagInfo[]>) => {
    const tagsToRemove: TagInfo[] = [];

    tagsByAxis.forEach((tagsInAxis, _) => {
      const uniqueTagInAxis = new Set(tagsInAxis);
      if (uniqueTagInAxis.size > 1) {
        const indexedTags = this._getIndexedTags(Array.from(uniqueTagInAxis));
        const sortedTags = this._sortIndexedTags(indexedTags);

        tagsToRemove.push(...sortedTags.slice(1));
      }
    });

    return tagsToRemove;
  };

  private _getIndexedTags = (tagsInAxis: TagInfo[]) => {
    return tagsInAxis.map((tag) => {
      const index = this.selectedTags.findIndex(
        (t) => t.name === tag.name && t.roleKey === tag.roleKey,
      );
      return { tag, index };
    });
  };

  private _sortIndexedTags = (
    indexedTags: { tag: TagInfo; index: number }[],
  ) => {
    return indexedTags
      .sort((a, b) => b.index - a.index)
      .map((item) => item.tag);
  };
}

export class TagsSelectionHierarchyManager {
  public selectTagsHierarchically = (
    selectedTags: TagInfo[],
    dimension: Dimension,
    tags?: AxisWithChildrenType[],
  ): { tags: MemberTagsViewType[] } => {
    if (tags === undefined) return { tags: [] };

    return {
      tags: this._processAxes(tags, dimension, selectedTags, dimension),
    };
  };

  private _processAxes = (
    axesArray: AxisWithChildrenType[],
    tagType: TaxonomyTagType,
    selectedTags: TagInfo[],
    dimension: Dimension,
  ): MemberTagsViewType[] => {
    return axesArray.map((axis) => ({
      ...axis,
      children: this._processMembers(
        axis.children,
        tagType,
        selectedTags,
        axis,
        dimension,
      ),
    }));
  };

  private _processMembers = (
    members: MemberType[],
    tagType: TaxonomyTagType,
    selectedTags: TagInfo[],
    axis: AxisWithChildrenBaseType,
    dimension: Dimension,
  ): MemberViewType[] => {
    return members.map((member) => {
      const isSelfSelected = this._isSelfSelected(
        selectedTags,
        tagType,
        member,
        axis,
        dimension,
      );

      const processedChildren = this._processMembers(
        member.children,
        tagType,
        selectedTags,
        axis,
        dimension,
      );

      const hasSelectedChild = this._hasSelectedChild(processedChildren);

      const selectionStatus = this._getSelectionStatus(
        isSelfSelected,
        hasSelectedChild,
      );

      return {
        ...member,
        isSelected: selectionStatus,
        children: processedChildren,
        parent: {
          id: axis.id,
          name: axis.name,
          label: axis.label,
          prefix: axis.prefix,
          type: tagType === "MEMBER" ? "AXIS" : "LINE_ITEMS",
        },
        role: axis.role,
        prefix: member.prefix,
        isSum: member.properties.isSum,
        isRecommended: member.properties.isRecommended,
      };
    });
  };

  private _isSelfSelected = (
    selectedTags: TagInfo[],
    tagType: TaxonomyTagType,
    member: MemberType,
    axis: AxisWithChildrenBaseType,
    dimension: Dimension,
  ) => {
    return selectedTags.some((tag) => {
      return (
        tag.type === member.type &&
        tag.name === member.name &&
        this._isSameParent(tag, axis, dimension)
      );
    });
  };

  private _isSameParent = (
    tag: TagInfo,
    axis: AxisWithChildrenBaseType,
    dimension: Dimension,
  ) => {
    if (dimension === "LINE_ITEM") {
      return tag.roleKey === axis.role?.key;
    } else if (dimension === "AXIS") {
      return tag.parent?.id === axis.id;
    }
  };

  private _hasSelectedChild = (children: MemberViewType[]) => {
    return children.some(
      (child) => child.isSelected === "self" || child.isSelected === "child",
    );
  };

  private _getSelectionStatus = (
    isSelfSelected: boolean,
    hasSelectedChild: boolean,
  ) => {
    return isSelfSelected ? "self" : hasSelectedChild ? "child" : false;
  };
}

const tagSelectionHierarchyManager = new TagsSelectionHierarchyManager();

export type TagsAndRole = {
  tags: MemberTagsViewType[];
  role: Role | null;
};

const groupTagsByRole = (tags: MemberTagsViewType[]) => {
  const grouped = new Map<string, TagsAndRole>();

  tags.forEach((tag) => {
    const key = tag.role?.key ?? "OTHERS";
    if (!grouped.has(key)) {
      grouped.set(key, {
        tags: [],
        role: tag?.role,
      });
    }
    grouped.get(key)!.tags.push(tag);
  });

  return grouped;
};

const useKeepTagsSingleSelected = ({
  dimension,
  tags,
}: {
  dimension: Dimension;
  tags?: MemberTagsViewType[];
}) => {
  const selectedFact = useMappingStore((state) => state.fact.selected);
  const { selectedMappedInfo, remove } = useSelectedTags();
  const selectedTags = selectedMappedInfo.tags ?? EMPTY_TAGS;

  useEffect(() => {
    const skipCondition = !selectedFact || !selectedTags.length || !tags;

    if (skipCondition) return;

    const tagSingleSelectedManager = new TagsSingleSelectedManager(
      selectedTags,
    );

    const tagsByAxis = tagSingleSelectedManager.getAxisTagsMap(tags, dimension);

    const tagsToRemove = tagSingleSelectedManager.findTagsToRemove(tagsByAxis);

    if (tagsToRemove.length > 0) {
      remove(factAddress(selectedFact), tagsToRemove);
    }
  }, [selectedTags, tags, dimension, selectedFact, remove]);
};

const useUpdateStatesFromServer = (
  period: AccountingPeriod | null,
  targetYear: TargetYear | null,
  accumulated: Accumulated | null,
  tags?: TagInfo[],
  fetchedForFactAddress?: string, // tags를 fetch할 때 사용된 selectedFact address (stale data 방지용)
) => {
  const selectedFact = useMappingStore((state) => state.fact.selected);
  const { add } = useSelectedTags();

  useEffect(() => {
    if (tags && selectedFact) {
      const currentFactAddress = factAddress(selectedFact);
      // fetch 시점의 fact와 현재 fact가 다르면 stale data이므로 무시
      if (
        fetchedForFactAddress &&
        fetchedForFactAddress !== currentFactAddress
      ) {
        return;
      }
      // 선택 직후 hydrate된 표시를 먼저 유지하고, 서버 응답은 병합해서 보강한다.
      add({
        address: currentFactAddress,
        tagInput: tags,
        period,
        targetYear,
        accumulated,
      });
    }
  }, [
    tags,
    selectedFact,
    add,
    fetchedForFactAddress,
    period,
    targetYear,
    accumulated,
  ]);
};

export const useTaggedCells = () => {
  const selectedFactFromStore =
    useMappingStore((state) => state.fact.selected) ?? undefined;
  const selectedFactAddress = selectedFactFromStore?.address;
  const { data: workbookId } = useWorkbookId();
  const {
    data: { tableId: tableId },
  } = useSelectedTable();

  // 현재 fact address 문자열
  const currentFactAddressString = selectedFactFromStore
    ? factAddress(selectedFactFromStore)
    : undefined;

  // 데이터가 실제로 fetch/캐시된 시점의 fact address 추적
  const prevDataUpdatedAtRef = useRef<number | undefined>(undefined);
  const factAddressAtDataUpdateRef = useRef<string | undefined>(undefined);

  const {
    data: tagged,
    isLoading: taggedLoading,
    isError: taggedError,
    isFetching: taggedFetching,
    refetch,
    dataUpdatedAt,
  } = useQuery({
    ...queries
      .workbook(workbookId!)
      ._ctx.tables(tableId!)
      ._ctx.cells(selectedFactAddress!)
      ._ctx.tags(),
    enabled: !!workbookId && !!tableId && !!selectedFactAddress,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 5,
  });

  // dataUpdatedAt이 바뀐 렌더 = 현재 fact를 위해 fetch가 새로 확정된 시점.
  // 그 시점의 fact address를 렌더 중 "동기적으로" 캡처한다.
  // (useLayoutEffect로 미루면 데이터가 도착한 첫 렌더에서 ref가 아직 직전 fact address를
  //  가리켜 fetchedForFactAddress가 stale이 되고, 그 stale 값이 useUpdateStatesFromServer의
  //  가드를 잘못 트리거해 서버가 준 정상 태그(parent 포함)의 store 반영을 막는다.
  //  ref 갱신은 리렌더를 유발하지 않아 그대로 고착된다.)
  if (dataUpdatedAt !== prevDataUpdatedAtRef.current) {
    prevDataUpdatedAtRef.current = dataUpdatedAt;
    if (dataUpdatedAt && currentFactAddressString) {
      factAddressAtDataUpdateRef.current = currentFactAddressString;
    }
  }

  return {
    data: tagged,
    isLoading: taggedLoading,
    isError: taggedError,
    refetch,
    fetchedForFactAddress: tagged
      ? factAddressAtDataUpdateRef.current
      : undefined, // 데이터가 있을 때만 해당 데이터의 fact address 반환
  };
};

export const useMappingSearchResultsData = (dimension: Dimension) => {
  const selectedFact = useMappingStore((state) => state.fact.selected?.address);

  const { dimension: currentDimension } = useMappingStore(
    (state) => state.dimension,
  );

  const { data: workbookId } = useWorkbookId();
  const {
    data: { tableId },
    isLoading: tableLoading,
    isError: tableError,
  } = useSelectedTable();

  const {
    data: tags,
    isLoading: tagsLoading,
    isError: tagsError,
    refetch: tagsRefetch,
  } = useQuery({
    ...queries
      .workbook(workbookId!)
      ._ctx.tables(tableId!)
      ._ctx.taxonomy()
      ._ctx.tags(dimension, selectedFact!),
    enabled:
      !!workbookId &&
      !!tableId &&
      !!selectedFact &&
      currentDimension === dimension,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 5,
  });

  const {
    data: tagged,
    isLoading: taggedLoading,
    isError: taggedError,
    refetch: taggedRefetch,
    fetchedForFactAddress,
  } = useTaggedCells();

  return {
    data: {
      alreadyTagged: tagged?.tags,
      tags,
    },
    isLoading: tableLoading || taggedLoading || tagsLoading,
    isError: tableError || taggedError || tagsError,
    refetch: () => {
      return Promise.all([taggedRefetch(), tagsRefetch()]);
    },
    fetchedForFactAddress, // stale data 방지를 위해 fetch 시점의 fact address 전달
  };
};

export const useMappingSearchResults = (dimension: Dimension) => {
  const { selectedMappedInfo } = useSelectedTags();
  const selectedTags = selectedMappedInfo.tags ?? EMPTY_TAGS;

  const {
    data: { alreadyTagged, tags },
    isLoading,
    isError,
    refetch,
    fetchedForFactAddress,
  } = useMappingSearchResultsData(dimension);

  useUpdateStatesFromServer(
    tags?.period ?? null,
    tags?.targetYear ?? null,
    tags?.accumulated ?? null,
    alreadyTagged,
    fetchedForFactAddress,
  );

  const processedData = useMemo(
    () =>
      tagSelectionHierarchyManager.selectTagsHierarchically(
        selectedTags,
        dimension,
        tags?.tags,
      ),
    [selectedTags, dimension, tags?.tags],
  );

  const tagsByRole = useMemo(
    () => groupTagsByRole(processedData.tags),
    [processedData.tags],
  );

  useKeepTagsSingleSelected({ dimension, tags: processedData.tags });

  return {
    data: tagsByRole,
    processedData,
    isLoading,
    isError,
    refetch,
  };
};
